import decoratorsJson from "../decorators.json";

import type {
  ClosingSymbol,
  ConstantNodeProps,
  DecoratorNodeProps,
  FunctionSuggestionProps,
  NodeJson,
  OpeningSymbol,
  OperatorSymbolNodeProps,
  SuggestionLabelJson,
  SymbolNodeProps,
  SymbolSuggestionProps,
  VariableNodeProps,
} from "../types";
import type { ExpressionNodePreviewType } from "../types/nodePreview";

/**
 * A preview node the builders can turn into node JSON, or one they leave out (`null`).
 */
export type PreviewNode =
  | ExpressionNodePreviewType
  | { type: "function" | "operator" | "operatorSymbol" | "group" };

/**
 * A parameter hole, optionally pre-filled with preview nodes. This is the web app's
 * `FunctionArgument`; the JSON files' `{}` and `{ type: "label" }` arguments satisfy it too.
 */
export type NodeJsonArgument = {
  nodes?: PreviewNode[];
};

/**
 * What every symbol-bearing item has: the glyph, its symbol props, and the labels and
 * decorators attached to the symbol. Shared by variables, constants and functions.
 */
export type SymbolInput = {
  symbol: string;
  symbolProps?: SymbolSuggestionProps;
  labels?: SuggestionLabelJson[];
  decorators?: Pick<DecoratorNodeProps, "name">[];
};

export type VariableInput = SymbolInput & {
  name?: string;
  props?: Partial<Omit<VariableNodeProps, "openingSymbol" | "closingSymbol">> & {
    arguments?: NodeJsonArgument[];
    openingSymbol?: { symbol: OpeningSymbol };
    closingSymbol?: { symbol: ClosingSymbol };
  };
};

export type ConstantInput = SymbolInput & {
  name?: string;
  props?: Partial<ConstantNodeProps>;
};

export type FunctionInput = SymbolInput & {
  name?: string;
  key?: string;
  props?: Omit<FunctionSuggestionProps, "arguments"> & { arguments?: NodeJsonArgument[] };
};

/**
 * The props without the keys whose value is undefined, so a node built from props equals the
 * same node read back from JSON, which never carries them.
 */
function definedProps<T extends object>(props: T): T {
  return Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined)) as T;
}

export function createSymbolNode(
  value: string,
  props: Partial<SymbolNodeProps> = {},
  children: NodeJson[] = [],
): NodeJson {
  return { type: "symbol", props: definedProps({ value, ...props }), children };
}

export function createInlineNodes(count: number): NodeJson[] {
  return Array.from({ length: count }, () => ({ type: "inline", props: {}, children: [] }));
}

export function createPreviewNode(n: PreviewNode): NodeJson | null {
  switch (n.type) {
    case "constant":
    case "variable":
      return {
        type: n.type,
        props: definedProps({ name: n.name, color: n.color }),
        children: [
          {
            type: "symbol",
            props: definedProps({ value: n.value ?? "", ...n.symbolProps }),
            children: [],
          },
        ],
      };
    case "number":
    case "plainText":
      return { type: n.type, props: { value: n.value ?? "" }, children: [] };
    case "symbol":
      return {
        type: "symbol",
        props: definedProps({ value: n.value ?? "", key: n.key }),
        children: [],
      };
    default:
      console.warn("unsupported preview node", n);
      return null;
  }
}

function createPreviewNodes(nodes: PreviewNode[] = []): NodeJson[] {
  return nodes.map(createPreviewNode).filter((n): n is NodeJson => n !== null);
}

export function createLabelNodes(labels: SuggestionLabelJson[] = []): NodeJson[] {
  return labels.map((label) => ({
    type: "label",
    props: definedProps({ position: label.position, name: label.name }),
    children: [{ type: "inline", props: {}, children: createPreviewNodes(label.nodes) }],
  }));
}

export function createDecoratorNodes(
  decorators: Pick<DecoratorNodeProps, "name">[] = [],
): NodeJson[] {
  return decorators.map((decorator) => ({
    type: "decorator",
    props: { name: decorator.name, key: decoratorsJson[decorator.name].key },
    children: [],
  }));
}

export function createParameterNodes(args?: NodeJsonArgument[]): NodeJson[] {
  if (!args) return [];
  return args.map((arg) => ({
    type: "inline",
    props: {},
    children: createPreviewNodes(arg.nodes),
  }));
}

function symbolWithAttachments(item: SymbolInput, withDecorators: boolean): NodeJson {
  return createSymbolNode(item.symbol, item.symbolProps, [
    ...createLabelNodes(item.labels),
    ...(withDecorators ? createDecoratorNodes(item.decorators) : []),
  ]);
}

export function buildSymbolNode(
  item: SymbolInput & { props?: Partial<SymbolNodeProps> },
): NodeJson {
  return createSymbolNode(item.symbol, item.props, [
    ...createLabelNodes(item.labels),
    ...createDecoratorNodes(item.decorators),
  ]);
}

export function buildVariableNode(item: VariableInput): NodeJson {
  return {
    type: "variable",
    props: definedProps({
      name: item.name,
      color: item.props?.color,
      openingSymbol: item.props?.openingSymbol?.symbol,
      closingSymbol: item.props?.closingSymbol?.symbol,
    }),
    children: [symbolWithAttachments(item, true), ...createParameterNodes(item.props?.arguments)],
  };
}

export function buildConstantNode(item: ConstantInput): NodeJson {
  return {
    type: "constant",
    props: definedProps({ name: item.name, color: item.props?.color }),
    children: [symbolWithAttachments(item, false)],
  };
}

/**
 * A function always gets at least one parameter hole.
 */
export function buildFunctionNode(item: FunctionInput): NodeJson {
  const args =
    item.props?.arguments && item.props.arguments.length > 0
      ? item.props.arguments
      : [{ nodes: [] }];

  return {
    type: "function",
    props: definedProps({
      name: item.name,
      isBuiltIn: item.props?.isBuiltIn,
      parenthesesVisibility: item.props?.parenthesesVisibility,
      placement: item.props?.placement,
      powerPlacement: item.props?.powerPlacement,
      key: item.key,
    }),
    children: [symbolWithAttachments(item, false), ...createParameterNodes(args)],
  };
}

export type OperatorSymbolInput = {
  name?: string;
  /**
   * The symbol key, put on the symbol child when `symbolProps` carries none.
   */
  key?: string;
  symbol: string;
  symbolProps?: SymbolSuggestionProps;
  labels?: SuggestionLabelJson[];
  props?: OperatorSymbolNodeProps;
};

export function buildOperatorSymbolNode(item: OperatorSymbolInput): NodeJson {
  return {
    type: "operatorSymbol",
    props: definedProps({ name: item.name, semanticType: item.props?.semanticType }),
    children: [
      createSymbolNode(
        item.symbol,
        item.symbolProps ?? { key: item.key },
        createLabelNodes(item.labels),
      ),
    ],
  };
}
